// Phase D joined: the default scene's composition, in one place for the page (src/sim/index.ts) and the Node tools. The
// streamed valley (src/world/) with the rpg layer on its fight and things, discovery (the hud stream's module), the
// story as a world addon, and the town wrapped round it all; one condition language (@rpg/story's) that the rpg layer,
// the town and the story register their facts on. The story reads the town's townsfolk (where each stands, its name and
// greeting), plays the town's cutscenes (`story:scene`), and gives, takes and trades through the rpg layer. No browser
// imports: the compiled ink comes in as `scripts`; the page applies the world's, the town's and the story's `?faults=`
// first. `?story=off` leaves the story out (the town stream's own checks: the townsfolk and the scenes alone).
import type { Runtime } from "@rpg/runtime";
import { Conditions } from "@rpg/story";
import type { SpikeContent } from "./content";
import { hudWorld, type HudWorld } from "./hud/scene";
import { storyScene } from "./story/scene";
import { metaScene } from "./meta/scene";
import type { StoryNpcs, StoryProviders, StoryRpg } from "./story/module";
import type { StoryScenes } from "./story";
import { CUTSCENE_ACTIONS } from "./town/cutscene";
import { townScene, type TownMount } from "./town/scene";
import { worldScene, type WorldMount } from "./world/scene";
import type { SceneName } from "./sim/host";

/** The joined valley's parts. */
export interface JoinedWorld {
  world: WorldMount;
  town: TownMount;
  /** The story (null with `?story=off`). */
  story: ReturnType<typeof storyScene> | null;
  /** Phase E1: the meta layer (achievements and the codex). */
  meta: ReturnType<typeof metaScene>;
  hud: HudWorld;
  conditions: Conditions;
  npcs: StoryNpcs;
  rpg: StoryRpg;
  /** The town's cutscenes as the story's handle and bot read them; `dispatch` is the kernel game's (the skip). */
  scenes(dispatch: (action: string, payload: unknown) => void): StoryScenes;
}

/** The default scene's composition. `params`: the page's switches (`seed`, `encounter`, `story`); `scripts`: the
 *  compiled ink by name; `providers`: the story's test providers (the provider-swap check); `name`: the scene's name
 *  (the world's saves carry it). */
export function joinedWorld(o: { content: SpikeContent; runtime: Runtime; params: URLSearchParams; difficulty?: string; scripts: Readonly<Record<string, string>>; providers?: StoryProviders; name?: SceneName }): JoinedWorld {
  const conditions = new Conditions();
  let town: TownMount | null = null;
  let world: WorldMount | null = null;
  const npcRows = new Map(o.content.registry.rows("npcs").map((n) => [n.id, n]));
  const npcs: StoryNpcs = {
    ids: () => [...npcRows.keys()],
    at: (id) => {
      if (!town || !npcRows.has(id)) return null;
      const n = town.folk().npc(id);
      return n.mode === "inside" ? null : [n.pos[0], n.pos[1], n.pos[2]];
    },
    yaw: (id) => (town && npcRows.has(id) ? town.folk().npc(id).yaw : 0),
    name: (id) => npcRows.get(id)?.name.id ?? `character.${id}`,
    greeting: (id) => npcRows.get(id)?.greeting.id ?? null,
  };
  const rpg: StoryRpg = {
    give: (item, count) => (item === "gold" ? world!.rpg().addGold(count, "story") : void world!.rpg().addItem(item, count, "story")),
    take: (item, count) => world!.rpg().take(item, count, "story"),
    shop: (npc) => void world!.rpg().openShop(npc),
  };
  const story = o.params.get("story") === "off" ? null : storyScene({ content: o.content, scripts: o.scripts, npcs, rpg, conditions, ...(o.providers ? { providers: o.providers } : {}) });
  const hud = hudWorld(o.content);
  // phase E1: the meta layer on the same conditions (its facts join them; its achievements ask through them)
  const meta = metaScene({ content: o.content, conditions });
  const seed = o.params.get("seed");
  world = worldScene({
    content: o.content,
    runtime: o.runtime,
    encounter: o.params.get("encounter") !== "off",
    ...(o.difficulty ? { difficulty: o.difficulty } : {}),
    ...(seed !== null && seed !== "" ? { seed: Number(seed) >>> 0 } : {}),
    ...(o.name ? { name: o.name } : {}),
    extra: hud.extra,
    addons: [...(story ? [story.addon] : []), meta.addon],
    conditions,
  });
  town = townScene(world, { content: o.content, runtime: o.runtime, params: o.params });
  const t = town;
  const scenes = (dispatch: (action: string, payload: unknown) => void): StoryScenes => ({
    playing: () => {
      const s = t.cutscenes().state();
      return [...(s.playing ? [s.playing.scene] : []), ...s.queue];
    },
    played: () => t.cutscenes().state().played.map((p) => p.scene),
    skip: () => dispatch(CUTSCENE_ACTIONS.skip, {}),
  });
  return { world, town, story, meta, hud, conditions, npcs, rpg, scenes };
}
